import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import CRMTenantBar from './CRMTenantBar';

const API = '/api';
const PRIMARY = '#6366f1';
const SUCCESS = '#22c55e';
const DANGER = '#ef4444';
const WARNING = '#f59e0b';
const BORDER = '#e5e7eb';
const BG = '#f8fafc';
const CARD = '#ffffff';
const TEXT = '#1e293b';
const MUTED = '#64748b';

function hdr() { return { Authorization: `Bearer ${localStorage.getItem('token')}` }; }

interface Tenant { id: number; company_name?: string; }

export default function CRMDashboard({ user, tenants = [] }: { user: any; tenants?: Tenant[] }) {
  const isSuperAdmin = !!(user?.role === 'super_admin' || user?.is_admin);
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(
    isSuperAdmin ? null : (user?.tenant_id ?? null)
  );
  const tenantId = isSuperAdmin ? selectedTenantId : (user?.tenant_id ?? null);
  const qs = tenantId !== null ? `?tenant_id=${tenantId}` : '';

  const [stats, setStats] = useState<any>(null);
  const [multiStats, setMultiStats] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/crm-integration/dashboard${qs}`, { headers: hdr() });
      if (r.data.multi_tenant) {
        setMultiStats(r.data.tenants || []);
        setStats(null);
      } else {
        setStats(r.data);
        setMultiStats([]);
      }
    } catch (e) {
      setStats(null); setMultiStats([]);
    } finally { setLoading(false); }
  }, [tenantId, qs]);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const s: Record<string, React.CSSProperties> = {
    page: { padding: 24, background: BG, minHeight: '100vh' },
    title: { fontSize: 22, fontWeight: 700, color: TEXT, margin: '0 0 4px' },
    sub: { color: MUTED, fontSize: 14, marginBottom: 20 },
    statGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16, marginBottom: 24 },
    stat: { background: CARD, borderRadius: 10, border: `1px solid ${BORDER}`, padding: '18px 20px' },
    statVal: { fontSize: 30, fontWeight: 800, color: PRIMARY },
    statLabel: { fontSize: 13, color: MUTED, marginTop: 4 },
    section: { background: CARD, borderRadius: 10, border: `1px solid ${BORDER}`, marginBottom: 20 },
    sectionHead: { padding: '14px 18px', borderBottom: `1px solid ${BORDER}`, fontSize: 15, fontWeight: 700, color: TEXT },
    table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
    th: { padding: '10px 16px', textAlign: 'left' as const, color: MUTED, fontWeight: 600, borderBottom: `1px solid ${BORDER}` },
    td: { padding: '10px 16px', color: TEXT, borderBottom: `1px solid ${BG}` },
  };

  const tenantName = isSuperAdmin && tenantId ? tenants.find(t => t.id === tenantId)?.company_name : null;

  return (
    <div style={s.page}>
      <h2 style={s.title}>CRM Dashboard</h2>
      <p style={s.sub}>{isSuperAdmin && !tenantId ? 'All-tenant overview — select a tenant to drill down' : `CRM health & activity${tenantName ? ` for ${tenantName}` : ''}`}</p>

      <CRMTenantBar
        isSuperAdmin={isSuperAdmin}
        tenants={tenants}
        selectedTenantId={selectedTenantId}
        onSelect={setSelectedTenantId}
        label="View tenant:"
      />

      {loading && <div style={{ color: MUTED, padding: 20 }}>Loading...</div>}

      {/* Super admin all-tenant view */}
      {isSuperAdmin && !tenantId && multiStats.length > 0 && (
        <div style={s.section}>
          <div style={s.sectionHead}>All Tenants — CRM Overview</div>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Tenant</th>
                <th style={s.th}>Connected CRMs</th>
                <th style={s.th}>API Calls (24h)</th>
                <th style={s.th}>Queue Pending</th>
                <th style={s.th}>Cache Entries</th>
              </tr>
            </thead>
            <tbody>
              {multiStats.map(t => (
                <tr key={t.tenant_id} style={{ cursor: 'pointer' }} onClick={() => setSelectedTenantId(t.tenant_id)}>
                  <td style={{ ...s.td, fontWeight: 600 }}>{t.tenant_name || `Tenant ${t.tenant_id}`}</td>
                  <td style={s.td}>{t.connected_crms ?? 0}</td>
                  <td style={s.td}>{t.api_calls_24h ?? 0}</td>
                  <td style={s.td}>{t.queue_pending ?? 0}</td>
                  <td style={s.td}>{t.cache_entries ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Single tenant view */}
      {stats && (
        <>
          <div style={s.statGrid}>
            <div style={s.stat}><div style={s.statVal}>{stats.connected_crms ?? 0}</div><div style={s.statLabel}>Connected CRMs</div></div>
            <div style={s.stat}><div style={s.statVal}>{stats.api_calls_24h ?? 0}</div><div style={s.statLabel}>API Calls (24h)</div></div>
            <div style={s.stat}><div style={{ ...s.statVal, color: (stats.avg_latency_ms ?? 0) < 500 ? SUCCESS : WARNING }}>{stats.avg_latency_ms ?? 0}ms</div><div style={s.statLabel}>Avg Latency</div></div>
            <div style={s.stat}><div style={s.statVal}>{stats.queue_pending ?? 0}</div><div style={s.statLabel}>Queue Pending</div></div>
            <div style={s.stat}><div style={s.statVal}>{stats.cache_entries ?? 0}</div><div style={s.statLabel}>Cache Entries</div></div>
            <div style={s.stat}><div style={{ ...s.statVal, color: (stats.error_rate_pct ?? 0) < 5 ? SUCCESS : DANGER }}>{stats.error_rate_pct ?? 0}%</div><div style={s.statLabel}>Error Rate</div></div>
          </div>

          {stats.connections?.length > 0 && (
            <div style={s.section}>
              <div style={s.sectionHead}>Provider Status</div>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Provider</th>
                    <th style={s.th}>Status</th>
                    <th style={s.th}>Last Health Check</th>
                    <th style={s.th}>Latency</th>
                    <th style={s.th}>Token Expires</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.connections.map((c: any) => {
                    const mins = c.token_expires_at ? Math.round((new Date(c.token_expires_at).getTime() - Date.now()) / 60000) : null;
                    return (
                      <tr key={c.id}>
                        <td style={{ ...s.td, fontWeight: 600, textTransform: 'capitalize' as const }}>{c.provider_key}</td>
                        <td style={s.td}>
                          <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                            background: c.status === 'connected' ? '#f0fdf4' : '#fef2f2',
                            color: c.status === 'connected' ? SUCCESS : DANGER }}>
                            {c.status}
                          </span>
                        </td>
                        <td style={s.td}>{c.last_health_check_at ? new Date(c.last_health_check_at).toLocaleString() : '—'}</td>
                        <td style={s.td}>{c.last_latency_ms ? `${c.last_latency_ms}ms` : '—'}</td>
                        <td style={{ ...s.td, color: mins !== null && mins < 30 ? DANGER : TEXT }}>
                          {mins !== null ? (mins < 0 ? 'Expired' : mins < 1440 ? `${Math.round(mins / 60)}h` : `${Math.round(mins / 1440)}d`) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {stats.recent_audits?.length > 0 && (
            <div style={s.section}>
              <div style={s.sectionHead}>Recent Activity</div>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Time</th>
                    <th style={s.th}>Action</th>
                    <th style={s.th}>Provider</th>
                    <th style={s.th}>User</th>
                    <th style={s.th}>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_audits.map((a: any, i: number) => (
                    <tr key={i}>
                      <td style={s.td}>{new Date(a.created_at).toLocaleString()}</td>
                      <td style={s.td}>{a.action}</td>
                      <td style={{ ...s.td, textTransform: 'capitalize' as const }}>{a.provider_key || '—'}</td>
                      <td style={s.td}>{a.username || `User ${a.user_id}`}</td>
                      <td style={s.td}>
                        <span style={{ color: a.success ? SUCCESS : DANGER, fontWeight: 600 }}>{a.success ? '✓' : '✗'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {!loading && !stats && multiStats.length === 0 && tenantId !== null && (
        <div style={{ textAlign: 'center', padding: 40, color: MUTED }}>No CRM data available for this tenant yet.</div>
      )}
    </div>
  );
}
